import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isBefore, isValid } from 'utils/dateUtils'

import {
  LOAD_MORE_CAMPAIGNS,
  RISK_LEVELS_VALUES,
  SET_EXCLUDED_PARTICIPANTS,
  SET_SELECTED_CAMPAIGNS,
  SET_SINGLE_CAMPAIGN_ID,
} from 'containers/SpearPhishingAgent/constants'
import {
  selectCampaignsData,
  selectExcludedParticipants,
  selectPlaceholderFilter,
} from 'containers/SpearPhishingAgent/selectors'
import { sortSuggestedParticipants } from 'containers/SpearPhishingAgent/utils'

import {
  trackPsimaCardSelection,
  trackPsimaParticipantEdit,
} from 'components/pages/SpearPhishingAgent/psimaPendo'

import { useMediaQuery } from '@uidotdev/usehooks'

const RISK_PRIORITY = {
  [RISK_LEVELS_VALUES.HIGH]: 3,
  [RISK_LEVELS_VALUES.MEDIUM]: 2,
  [RISK_LEVELS_VALUES.LOW]: 1,
}

const buildParticipantsData = (campaignId, suggestedProfiles, frozenOrderIds) => {
  const profilesById = suggestedProfiles.reduce((acc, profile) => {
    acc[profile.id] = {
      ...profile,
      campaignId,
    }
    return acc
  }, {})

  if (!frozenOrderIds) {
    return suggestedProfiles.map((profile) => profilesById[profile.id])
  }

  const frozenProfiles = frozenOrderIds
    .map((participantId) => profilesById[participantId])
    .filter(Boolean)
  const frozenIdSet = new Set(frozenOrderIds)
  const newlyAddedProfiles = sortSuggestedParticipants(
    suggestedProfiles.filter((profile) => !frozenIdSet.has(profile.id))
  ).map((profile) => profilesById[profile.id])

  return [...frozenProfiles, ...newlyAddedProfiles]
}

const useCardSection = ({ setIsQuickLaunchModalOpen }) => {
  const dispatch = useDispatch()
  const campaignsData = useSelector(selectCampaignsData)
  const excludedParticipants = useSelector(selectExcludedParticipants)
  const placeholderFilter = useSelector(selectPlaceholderFilter)
  const [expandedCampaignId, setExpandedCampaignId] = useState(null)
  // Participant order is frozen per card at expand time so check/uncheck never re-sorts.
  const [frozenParticipantOrderByCampaignId, setFrozenParticipantOrderByCampaignId] = useState({})
  const [sentinelNode, setSentinelNode] = useState(null)
  const [isSentinelVisible, setIsSentinelVisible] = useState(false)
  const isMobile = useMediaQuery('(max-width: 1000px)')

  const data = useMemo(
    () =>
      campaignsData.items.map(({ id, audience }) =>
        buildParticipantsData(
          id,
          audience.suggestedProfiles,
          frozenParticipantOrderByCampaignId[id]
        )
      ),
    [campaignsData.items, frozenParticipantOrderByCampaignId]
  )

  const { checkedCardsByParticipant, cardCountByParticipant } = useMemo(() => {
    const checkedCardsByParticipantMap = {}
    const cardCountByParticipantMap = {}

    campaignsData.items.forEach((item) => {
      if (!campaignsData.selectedCampaigns.includes(item.id)) return

      item.audience.suggestedProfiles.forEach(({ id }) => {
        cardCountByParticipantMap[id] = (cardCountByParticipantMap[id] || 0) + 1

        if (!excludedParticipants[item.id]?.includes(id)) {
          checkedCardsByParticipantMap[id] = [...(checkedCardsByParticipantMap[id] || []), item.id]
        }
      })
    })

    return {
      checkedCardsByParticipant: checkedCardsByParticipantMap,
      cardCountByParticipant: cardCountByParticipantMap,
    }
  }, [campaignsData.items, campaignsData.selectedCampaigns, excludedParticipants])

  const getParticipantDuplicateState = useCallback(
    (campaignId, participantId) => {
      if (!campaignsData.selectedCampaigns.includes(campaignId)) return null
      if ((cardCountByParticipant[participantId] || 0) < 2) return null

      const checkedCards = checkedCardsByParticipant[participantId] || []
      if (checkedCards.length === 0) return null

      return checkedCards.includes(campaignId) ? 'owner' : 'loser'
    },
    [campaignsData.selectedCampaigns, cardCountByParticipant, checkedCardsByParticipant]
  )

  const setExcludedParticipant = (campaignId, participantId) => {
    const isExcludedHere = excludedParticipants[campaignId]?.includes(participantId)
    const newExcluded = { ...excludedParticipants }

    trackPsimaParticipantEdit()

    if (isExcludedHere) {
      newExcluded[campaignId] = excludedParticipants[campaignId].filter(
        (id) => id !== participantId
      )

      campaignsData.items.forEach((item) => {
        if (item.id === campaignId) return
        if (!campaignsData.selectedCampaigns.includes(item.id)) return

        const hasParticipant = item.audience.suggestedProfiles.some(
          ({ id }) => id === participantId
        )

        if (hasParticipant && !newExcluded[item.id]?.includes(participantId)) {
          newExcluded[item.id] = [...(newExcluded[item.id] || []), participantId]
        }
      })
    } else {
      newExcluded[campaignId] = [...(excludedParticipants[campaignId] || []), participantId]

      const ownershipOrder = campaignsData.items
        .filter(
          (item) =>
            campaignsData.selectedCampaigns.includes(item.id) &&
            item.audience.suggestedProfiles.some(({ id }) => id === participantId)
        )
        .map((item) => ({
          id: item.id,
          priority: RISK_PRIORITY[item.level] || 0,
          fitScore:
            item.audience.suggestedProfiles.find(({ id }) => id === participantId)?.fitScore ?? 0,
        }))
        .sort(
          (firstCard, secondCard) =>
            secondCard.priority - firstCard.priority || secondCard.fitScore - firstCard.fitScore
        )

      const currentIndex = ownershipOrder.findIndex(({ id }) => id === campaignId)
      const nextOwner = ownershipOrder[currentIndex + 1]

      if (nextOwner) {
        newExcluded[nextOwner.id] = (newExcluded[nextOwner.id] || []).filter(
          (id) => id !== participantId
        )
      }
    }

    dispatch({
      type: SET_EXCLUDED_PARTICIPANTS,
      payload: { excludedParticipants: newExcluded },
    })
  }

  const claimAllParticipantsForCampaign = (campaignId) => {
    const campaign = campaignsData.items.find((item) => item.id === campaignId)

    if (!campaign) return

    trackPsimaParticipantEdit()

    const participantIds = campaign.audience.suggestedProfiles.map(({ id }) => id)
    const newExcluded = { ...excludedParticipants, [campaignId]: [] }

    campaignsData.items.forEach((item) => {
      if (item.id === campaignId) return
      if (!campaignsData.selectedCampaigns.includes(item.id)) return

      const overlappingIds = item.audience.suggestedProfiles
        .map(({ id }) => id)
        .filter((id) => participantIds.includes(id))

      if (overlappingIds.length === 0) return

      newExcluded[item.id] = Array.from(
        new Set([...(newExcluded[item.id] || []), ...overlappingIds])
      )
    })

    dispatch({
      type: SET_EXCLUDED_PARTICIPANTS,
      payload: { excludedParticipants: newExcluded },
    })
  }

  const toggleAllParticipants = (campaignId, participantIds) => {
    const excludedHere = excludedParticipants[campaignId] || []
    const isAllSelected = excludedHere.length === 0

    if (!isAllSelected) {
      claimAllParticipantsForCampaign(campaignId)
      return
    }

    trackPsimaParticipantEdit()

    dispatch({
      type: SET_EXCLUDED_PARTICIPANTS,
      payload: {
        excludedParticipants: {
          ...excludedParticipants,
          [campaignId]: [...participantIds],
        },
      },
    })
  }

  const setSelectedCampaign = (id) => {
    const isSelected = campaignsData.selectedCampaigns.includes(id)
    const selectedCampaigns = isSelected
      ? campaignsData.selectedCampaigns.filter((campaign) => campaign !== id)
      : [...campaignsData.selectedCampaigns, id]

    trackPsimaCardSelection({ selected: !isSelected })

    dispatch({
      type: SET_SELECTED_CAMPAIGNS,
      payload: {
        selectedCampaigns,
      },
    })
  }

  const scrollToCampaignCard = useCallback((campaignId) => {
    requestAnimationFrame(() => {
      const cardElement = document.getElementById(`psima-dashboard-card-${campaignId}`)

      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    })
  }, [])

  const toggleDetails = (id) => {
    const isOpening = expandedCampaignId !== id

    if (isOpening) {
      const campaign = campaignsData.items.find((item) => item.id === id)
      const sortedParticipantIds = sortSuggestedParticipants(
        campaign?.audience?.suggestedProfiles || []
      ).map((profile) => profile.id)

      setFrozenParticipantOrderByCampaignId((prevOrder) => ({
        ...prevOrder,
        [id]: sortedParticipantIds,
      }))
      scrollToCampaignCard(id)
    }

    setExpandedCampaignId((prevExpandedId) => (prevExpandedId === id ? null : id))
  }

  const isValidUntilExpired = (expiresAt) => {
    const validUntilDate = new Date(expiresAt)

    if (!isValid(validUntilDate)) {
      return false
    }

    return isBefore(validUntilDate, new Date())
  }

  const launchSimulation = (id) => {
    dispatch({
      type: SET_SINGLE_CAMPAIGN_ID,
      payload: {
        value: id,
      },
    })

    setIsQuickLaunchModalOpen(true)
  }

  useEffect(() => {
    if (!sentinelNode) return

    const observer = new IntersectionObserver(
      ([entry]) => setIsSentinelVisible(entry.isIntersecting),
      { threshold: 0.1 }
    )

    observer.observe(sentinelNode)

    return () => observer.disconnect()
  }, [sentinelNode])

  useEffect(() => {
    if (
      isSentinelVisible &&
      campaignsData.hasMore &&
      !campaignsData.loadMoreLoader &&
      !campaignsData.loader &&
      campaignsData.items.length > 0
    ) {
      setIsSentinelVisible(false)
      dispatch({ type: LOAD_MORE_CAMPAIGNS })
    }
  }, [
    isSentinelVisible,
    campaignsData.hasMore,
    campaignsData.loadMoreLoader,
    campaignsData.loader,
    campaignsData.items.length,
    dispatch,
  ])

  return {
    campaignsData,
    excludedParticipants,
    placeholderFilter,
    expandedCampaignId,
    setSentinelNode,
    isMobile,
    data,
    setExcludedParticipant,
    toggleAllParticipants,
    claimAllParticipantsForCampaign,
    getParticipantDuplicateState,
    setSelectedCampaign,
    toggleDetails,
    isValidUntilExpired,
    launchSimulation,
  }
}

export default useCardSection
